import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import {
  PublicProductNameComponent,
  PublicSubscriptionDataSourceService,
  PublicSubscriptionDetails,
  SubscriptionNumber,
  SubscriptionStatusComponent,
} from '@web/shared/module/shop';
import { Divider } from 'primeng/divider';
import {
  AssetImage,
  CurrencyComponent,
  DateComponent,
  DetailsViewComponent,
  DrawerContentComponent,
  DrawerTitleComponent,
  Icon,
  IconComponent,
  ImageThumbnailComponent,
  NoDataComponent,
  PrimengDividerDirective,
  PrimengDrawerDirective,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { isNotNil, Nillable, once } from '@web/utils';
import { PublicSubscriptionOrderListComponent } from '../public-subscription-order-list/public-subscription-order-list.component';
import { Drawer } from 'primeng/drawer';
import { PublicSubscriptionBillingAddressEditComponent } from '../public-subscription-billing-address-edit/public-subscription-billing-address-edit.component';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { PublicSubscriptionShippingAddressEditComponent } from '../public-subscription-shipping-address-edit/public-subscription-shipping-address-edit.component';

@Component({
  selector: 'app-public-subscription-details',
  imports: [
    CommonModule,
    Divider,
    PrimengDividerDirective,
    SpinnerFilledComponent,
    DetailsViewComponent,
    CurrencyComponent,
    DateComponent,
    NoDataComponent,
    SubscriptionStatusComponent,
    ImageThumbnailComponent,
    PublicProductNameComponent,
    NgOptimizedImage,
    PublicSubscriptionOrderListComponent,
    Drawer,
    DrawerTitleComponent,
    PrimengDrawerDirective,
    DrawerContentComponent,
    PublicSubscriptionBillingAddressEditComponent,
    Button,
    IconComponent,
    Tooltip,
    PublicSubscriptionShippingAddressEditComponent,
  ],
  templateUrl: './public-subscription-details.component.html',
  styleUrl: './public-subscription-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicSubscriptionDetailsComponent implements OnInit {
  public readonly number = input.required<SubscriptionNumber>();

  protected readonly data = signal<Nillable<PublicSubscriptionDetails>>(null);
  protected readonly number$ = toObservable(this.number);
  protected readonly billingAddressEditVisibility = signal<boolean>(false);
  protected readonly shippingAddressEditVisibility = signal<boolean>(false);
  protected readonly AssetImage = AssetImage;
  protected readonly isNotNil = isNotNil;
  protected readonly Icon = Icon;

  /* prettier-ignore */
  private readonly subscriptionDataSource = inject(PublicSubscriptionDataSourceService);

  public readonly customCloseBillingAddressEditEvent = () => {
    this.closeBillingAddressEditDialog();
  };

  public ngOnInit(): void {
    this.fetchData();
  }

  public handleCancelBillingAddressEditEvent(): void {
    this.closeBillingAddressEditDialog();
  }

  public handleCancelShippingAddressEditEvent(): void {
    this.closeShippingAddressEditDialog();
  }

  public handleSubmitBillingAddressEditEvent(): void {
    this.closeBillingAddressEditDialog();
    this.fetchData();
  }

  public handleSubmitShippingAddressEditEvent(): void {
    this.closeShippingAddressEditDialog();
    this.fetchData();
  }

  public handleBillingAddressUpdateClick(): void {
    this.showBillingAddressEditDialog();
  }

  public handleShippingAddressUpdateClick(): void {
    this.showShippingAddressEditDialog();
  }

  protected readonly customCloseShippingAddressEditEvent = () => {
    this.closeShippingAddressEditDialog();
  };

  private closeBillingAddressEditDialog(): void {
    this.billingAddressEditVisibility.set(false);
  }

  private closeShippingAddressEditDialog(): void {
    this.shippingAddressEditVisibility.set(false);
  }

  private showBillingAddressEditDialog(): void {
    this.billingAddressEditVisibility.set(true);
  }

  private showShippingAddressEditDialog(): void {
    this.shippingAddressEditVisibility.set(true);
  }

  private fetchData(): void {
    this.number$
      .pipe(
        once(),
        switchMap((number) =>
          this.subscriptionDataSource.fetchDetailsByNumber(number),
        ),
      )
      .subscribe((data) => this.data.set(data));
  }
}
